import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Sparkles, Users, MessagesSquare, Activity, TrendingUp, ShieldCheck, Bot, Calendar, FileText, ArrowUpRight, Play, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import CinematicWalkthrough from "./CinematicWalkthrough";
import AIEmployeesPreview from "./AIEmployeesPreview";

const ICONS = { "AI-handled conversations": MessagesSquare, "Qualified leads": Users, "Avg. conversation depth": Activity, "Knowledge base": FileText, "Showcase conversations": Sparkles };

const FunnelBar = ({ row, max }) => {
  const widthPct = max > 0 ? Math.max(4, Math.round((row.count / max) * 100)) : 4;
  const color = {
    High: "bg-emerald-500",
    Medium: "bg-amber-500",
    Marketing: "bg-sky-500",
    Lost: "bg-rose-400",
    Junk: "bg-stone-400",
  }[row.status] || "bg-muted-foreground";
  return (
    <div data-testid={`funnel-row-${row.status.toLowerCase()}`} className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <div className="font-medium text-foreground">{row.status}</div>
        <div className="text-muted-foreground">{row.label} · <span className="font-semibold text-foreground">{row.count}</span></div>
      </div>
      <div className="h-2.5 bg-muted/60 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all duration-700`}
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
};

const HeatCell = ({ count, max }) => {
  const intensity = max > 0 ? count / max : 0;
  let bg;
  if (count === 0) bg = "bg-muted/30";
  else if (intensity < 0.34) bg = "bg-emerald-200";
  else if (intensity < 0.67) bg = "bg-emerald-400";
  else bg = "bg-emerald-600";
  return (
    <div className={`h-9 ${bg} rounded grid place-items-center text-[11px] font-medium ${intensity >= 0.67 ? "text-white" : "text-foreground/70"}`}>
      {count > 0 ? count : ""}
    </div>
  );
};

const ScoreGauge = ({ score, grade }) => {
  const arc = Math.max(0, Math.min(100, score));
  // Half-circle SVG gauge
  const radius = 80;
  const circ = Math.PI * radius;
  const offset = circ - (circ * arc) / 100;
  const gradeColor = arc >= 85 ? "text-emerald-600" : arc >= 70 ? "text-sky-600" : arc >= 50 ? "text-amber-600" : "text-rose-500";
  return (
    <div className="flex flex-col items-center" data-testid="ai-engagement-gauge">
      <svg viewBox="0 0 200 110" className="w-full max-w-[260px]" aria-hidden>
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="hsl(var(--muted))" strokeWidth="14" strokeLinecap="round"/>
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke="url(#gauge-grad)"
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1s ease-out" }}
        />
        <defs>
          <linearGradient id="gauge-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0E5C4E"/>
            <stop offset="100%" stopColor="#10b981"/>
          </linearGradient>
        </defs>
      </svg>
      <div className="-mt-6 text-center">
        <div className="font-display text-5xl font-semibold tabular-nums">{arc}</div>
        <div className={`text-sm font-medium uppercase tracking-wider ${gradeColor}`}>{grade}</div>
      </div>
    </div>
  );
};

export default function ShowcaseDashboard({ data, link, slug, isReadOnly, viewerRole, onReset }) {
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  async function handleReset() {
    setResetting(true);
    try {
      const r = await api.post("/dashboard/demo/reset");
      toast.success(`Demo data reset · ${r.data.seeded.leads} leads · ${r.data.seeded.conversations} conversations`);
      onReset?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
      setConfirmReset(false);
    }
  }

  if (!data) return null;
  const { lead_funnel, lead_funnel_total, ai_engagement, heatmap, executive_summary, company } = data;
  const maxLead = Math.max(1, ...lead_funnel.map((r) => r.count));
  const maxHeat = Math.max(1, ...heatmap.flatMap((row) => row.cells.map((c) => c.count)));
  const dayParts = ["Night", "Morning", "Afternoon", "Evening"];

  return (
    <div className="space-y-8" data-testid="showcase-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-[#0E5C4E] via-[#0E5C4E] to-[#10745f] text-white p-8 sm:p-10 shadow-[0_20px_60px_-30px_rgba(14,92,78,0.55)]">
        <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_20%_-10%,white,transparent_40%),radial-gradient(circle_at_85%_115%,white,transparent_40%)]"/>
        <div className="relative flex flex-wrap items-start justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 backdrop-blur rounded-full text-xs font-medium uppercase tracking-wider" data-testid="showcase-demo-pill">
              <Sparkles className="w-3.5 h-3.5" /> Gold Demo Tenant · Healthcare
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 leading-tight">
              {company?.name || "Rudras-AI Healthcare Solutions"}
            </h1>
            <p className="mt-3 text-white/80 text-base max-w-xl">
              A live, fully-seeded showcase tenant — explore lead intelligence, AI engagement, and an executive view tailored for a healthcare SaaS prospect demo.
            </p>
            {isReadOnly && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-300/40 rounded-full text-amber-100 text-xs" data-testid="readonly-banner">
                <ShieldCheck className="w-3.5 h-3.5" /> Read-only viewer · Mutations disabled
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 min-w-[200px]">
            <Button
              onClick={() => setWalkthroughOpen(true)}
              className="w-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300 font-medium"
              data-testid="showcase-cinematic-btn"
            >
              <Play className="w-4 h-4 mr-2"/> Cinematic Walkthrough
            </Button>
            {link && (
              <a href={link} target="_blank" rel="noreferrer">
                <Button variant="secondary" className="w-full bg-white text-[#0E5C4E] hover:bg-white/90" data-testid="showcase-open-chat">
                  <Bot className="w-4 h-4 mr-2"/> Open Sara
                </Button>
              </a>
            )}
            <Link to="/dashboard/leads">
              <Button variant="outline" className="w-full border-white/40 text-white hover:bg-white/10 hover:text-white" data-testid="showcase-view-leads">
                <Users className="w-4 h-4 mr-2"/> View leads
              </Button>
            </Link>
            {!isReadOnly && (
              <Button
                variant="ghost"
                onClick={() => setConfirmReset(true)}
                className="w-full text-white/80 hover:bg-white/10 hover:text-white text-xs"
                data-testid="showcase-reset-btn"
                disabled={resetting}
              >
                <RotateCcw className={`w-3.5 h-3.5 mr-1.5 ${resetting ? "animate-spin" : ""}`}/>
                {resetting ? "Resetting…" : "Reset demo data"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Executive summary strip */}
      <section
        className="rounded-2xl border border-[#0E5C4E]/10 bg-gradient-to-br from-emerald-50/40 via-white to-white p-5 sm:p-6"
        data-testid="exec-summary-section"
      >
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0E5C4E]/80">Executive snapshot</div>
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 mt-0.5">Performance at a glance</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="exec-summary">
          {executive_summary.map((b) => {
            const Icon = ICONS[b.label] || TrendingUp;
            return (
              <Card key={b.label} className="p-4 hover:shadow-md transition border-slate-200/70" data-testid={`exec-stat-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
                  <Icon className="w-3.5 h-3.5"/>{b.label}
                </div>
                <div className="font-display text-2xl sm:text-3xl font-semibold mt-1 tabular-nums">{b.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{b.sub}</div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Lead funnel + AI engagement */}
      <section
        className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-slate-50/60 via-white to-white p-5 sm:p-6"
        data-testid="lead-engagement-section"
      >
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Pipeline & engagement</div>
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 mt-0.5">Where prospects stand right now</h2>
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 border-slate-200/70" data-testid="lead-funnel-card">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-display text-xl font-semibold">Lead funnel</h3>
                <p className="text-sm text-muted-foreground">{lead_funnel_total} total prospects · by qualification stage</p>
              </div>
              <Link to="/dashboard/leads">
                <Button variant="ghost" size="sm" data-testid="funnel-open-leads">All leads <ArrowUpRight className="w-3.5 h-3.5 ml-1"/></Button>
              </Link>
            </div>
            <div className="space-y-4">
              {lead_funnel.map((row) => <FunnelBar key={row.status} row={row} max={maxLead} />)}
            </div>
          </Card>

          <Card className="p-6 border-slate-200/70" data-testid="ai-engagement-card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-xl font-semibold">AI engagement</h3>
              <Badge variant="outline" className="text-xs">{ai_engagement.grade}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-3">Composite of conversation depth, lead capture, and volume.</p>
            <ScoreGauge score={ai_engagement.score} grade={ai_engagement.grade} />
            <div className="grid grid-cols-3 gap-2 mt-2 text-center">
              <div>
                <div className="font-semibold tabular-nums">{ai_engagement.total_conversations}</div>
                <div className="text-[11px] text-muted-foreground">Convos</div>
              </div>
              <div>
                <div className="font-semibold tabular-nums">{ai_engagement.avg_turns}</div>
                <div className="text-[11px] text-muted-foreground">Avg turns</div>
              </div>
              <div>
                <div className="font-semibold tabular-nums">{ai_engagement.capture_rate_pct}%</div>
                <div className="text-[11px] text-muted-foreground">Capture</div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Chat activity heatmap */}
      <section
        className="rounded-2xl border border-amber-200/40 bg-gradient-to-br from-amber-50/40 via-white to-white p-5 sm:p-6"
        data-testid="chat-activity-section"
      >
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700/80">Activity rhythm</div>
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 mt-0.5">When prospects are talking to Sara</h2>
          </div>
        </div>
        <Card className="p-6 border-slate-200/70" data-testid="heatmap-card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display text-xl font-semibold">Chat activity</h3>
              <p className="text-sm text-muted-foreground">Last 7 days · by time of day</p>
            </div>
            <Calendar className="w-5 h-5 text-muted-foreground"/>
          </div>
          <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
            <div></div>
            <div className="grid grid-cols-4 gap-2 text-[11px] text-muted-foreground text-center">
              {dayParts.map((p) => <div key={p}>{p}</div>)}
            </div>
            {heatmap.map((row) => (
              <React.Fragment key={row.date}>
                <div className="text-xs text-muted-foreground">
                  <div className="font-medium text-foreground">{row.day}</div>
                  <div>{row.date.slice(5)}</div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {row.cells.map((c, i) => <HeatCell key={i} count={c.count} max={maxHeat}/>)}
                </div>
              </React.Fragment>
            ))}
          </div>
        </Card>
      </section>

      <CinematicWalkthrough
        open={walkthroughOpen}
        onClose={() => setWalkthroughOpen(false)}
        showcase={data}
      />

      {/* Slice M+ (Feb 2026) — Multi-AI Employee preview teaser. */}
      <AIEmployeesPreview />

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset demo data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all current leads, conversations, documents, and prospect profiles for the demo tenant, then re-seed the fresh 25 leads · 20 conversations · 17 knowledge-base chunks. Ideal between video takes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} data-testid="reset-confirm" disabled={resetting}>
              {resetting ? "Resetting…" : "Yes, reset"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
